import { itemDrafts } from "../lib/itemDrafts.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api.js";
import type { Principal } from "../types.js";

async function fetchSession(): Promise<Principal | null> {
  try {
    const response = await api.get<{ principal: Principal }>("/auth/me");
    return response.principal;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { identifier: string; password: string }) =>
      api.post<{ user: { username: string } }>("/auth/login", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

/** Self-service password change; the server keeps this session alive. */
export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ user: { id: string } }>("/auth/password", input),
    onSuccess: () => {
      // The session survives, but re-read it so the UI reflects the
      // re-issued principal instead of trusting a stale cache.
      void queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      itemDrafts.clear();
      // Drop every cached query except the session...
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
      // ...then explicitly set the session to signed-out. Unlike clear(), this
      // notifies mounted observers, so the app returns to the login screen
      // immediately instead of showing stale inventory until a refresh.
      queryClient.setQueryData(["session"], null);
    },
  });
}
